import { HoaFee, type IBaseHoaFee } from '@condo/domain';
import type { UpsertHoaFeeMutationRequest } from '@condo/sdk/propx';
import { getClient } from './api-client';

/**
 * Get a single HOA fee by ID
 * @param hoaFeeId - The ID of the HOA fee to retrieve
 * @returns Promise<HoaFee> - The HOA fee data
 */
export const fetchHoaFee = async (hoaFeeId: number): Promise<HoaFee> => {
    return getClient('propx')
        .getHoaFee({ hoaFeeId })
        .then(hoaFee => new HoaFee(hoaFee));
};

/**
 * Get all HOA fees for an estate
 * @param estateId - The ID of the estate
 * @returns Promise<HoaFee[]> - Array of HOA fees for the estate
 */
export const getHoaFeesByEstate = async (estateId: number): Promise<HoaFee[]> => {
    return getClient('propx')
        .getHoaFeesByEstate({ estateId })
        .then(hoaFees => hoaFees.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(hoaFee => new HoaFee(hoaFee)));
};

/**
 * Create or update a HOA fee (upsert)
 * @param params - Object containing estateId, hoaFee data, and userUid
 * @returns Promise<HoaFee> - The created or updated HOA fee data
 */
export const upsertHoaFee = async (hoaFee: IBaseHoaFee): Promise<HoaFee> => {
    return getClient('propx')
        .upsertHoaFee(hoaFee as UpsertHoaFeeMutationRequest)
        .then(hoaFee => new HoaFee(hoaFee));
};

/**
 * Delete a HOA fee
 * @param hoaFeeId - The ID of the HOA fee to delete
 * @returns Promise<void>
 */
export const deleteHoaFee = async (hoaFeeId: number): Promise<void> => getClient('propx').deleteHoaFee({ hoaFeeId });
